<script lang="ts">
  import { t } from '$core/i18n';
  import {
    getGlobalMemoryWeights,
    upsertGlobalMemoryWeights,
    listMemoryWeights,
    deleteMemoryWeightsForNode,
    listNodes
  } from '$db/local/repositories';
  import type { Node, MemoryWeights } from '$domain/types';
  import { Modal } from '$ui/primitives/Modal';
  import { Button } from '$ui/primitives/Button';
  import { Badge } from '$ui/primitives/Badge';
  import { Icon } from '$ui/primitives/Icon';
  import { EmptyState } from '$ui/primitives/EmptyState';

  let { onClose }: { onClose: () => void } = $props();

  const MIN_RETENTION = 0.70;
  const MAX_RETENTION = 0.97;
  const STEP_RETENTION = 0.01;
  const DEFAULT_RETENTION = 0.9;

  const MIN_INTERVAL = 7;
  const MAX_INTERVAL = 365;
  const STEP_INTERVAL = 7;
  const DEFAULT_INTERVAL = 365;

  let loading = $state(true);
  let busy = $state(false);
  let message = $state<string | null>(null);

  let globalRetention = $state(DEFAULT_RETENTION);
  let globalInterval = $state(DEFAULT_INTERVAL);
  let initialGlobalRetention = $state(DEFAULT_RETENTION);
  let initialGlobalInterval = $state(DEFAULT_INTERVAL);

  let subjects = $state<Node[]>([]);
  let weights = $state<MemoryWeights[]>([]);

  let globalDirty = $derived(
    Math.abs(globalRetention - initialGlobalRetention) > 0.0001 ||
      globalInterval !== initialGlobalInterval
  );

  interface SubjectRow {
    subject: Node;
    effectiveRetention: number;
    effectiveInterval: number;
    hasOverride: boolean;
    overrideId: string | null;
  }

  let rows = $derived(buildRows());

  function buildRows(): SubjectRow[] {
    const globalRow = weights.find(
      (w) => !w.nodeId && !w.deletedAt
    );
    const globalR = globalRow?.retentionTarget ?? DEFAULT_RETENTION;
    const globalI = globalRow?.maximumIntervalDays ?? DEFAULT_INTERVAL;

    return subjects.map((subject) => {
      const subjectRow = weights.find(
        (w) =>
          !w.deletedAt &&
          w.nodeId === subject.id
      );
      if (subjectRow) {
        return {
          subject,
          effectiveRetention: subjectRow.retentionTarget,
          effectiveInterval:
            subjectRow.maximumIntervalDays ?? globalI,
          hasOverride: true,
          overrideId: subjectRow.id
        };
      }
      return {
        subject,
        effectiveRetention: globalR,
        effectiveInterval: globalI,
        hasOverride: false,
        overrideId: null
      };
    });
  }

  async function refresh(): Promise<void> {
    const [globalRow, weightsList, subjectsList] = await Promise.all([
      getGlobalMemoryWeights(),
      listMemoryWeights(),
      listNodes()
    ]);
    if (globalRow) {
      globalRetention = globalRow.retentionTarget;
      globalInterval = globalRow.maximumIntervalDays ?? DEFAULT_INTERVAL;
    } else {
      globalRetention = DEFAULT_RETENTION;
      globalInterval = DEFAULT_INTERVAL;
    }
    initialGlobalRetention = globalRetention;
    initialGlobalInterval = globalInterval;
    weights = weightsList;
    subjects = subjectsList;
    loading = false;
  }

  $effect(() => {
    void refresh();
  });

  async function saveGlobal(): Promise<void> {
    if (busy || !globalDirty) return;
    busy = true;
    message = null;
    try {
      await upsertGlobalMemoryWeights({
        retentionTarget: globalRetention,
        maximumIntervalDays: globalInterval
      });
      initialGlobalRetention = globalRetention;
      initialGlobalInterval = globalInterval;
      message = t('fsrsAdvanced.saved');
      await refresh();
    } finally {
      busy = false;
    }
  }

  async function resetSubjectOverride(subjectId: string): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      await deleteMemoryWeightsForNode(subjectId);
      await refresh();
    } finally {
      busy = false;
    }
  }

  function fmtRetention(v: number): string {
    return `${Math.round(v * 100)}%`;
  }

  function fmtInterval(v: number): string {
    return `${v}${t('subjectSettings.daysSuffix')}`;
  }

  let open = $state(true);
</script>

<Modal
  bind:open
  title={t('fsrsAdvanced.title')}
  size="lg"
  onclose={onClose}
>
  {#if loading}
    <p class="loading">{t('fsrsAdvanced.loading')}</p>
  {:else}
    <div class="body">
      <section class="field">
        <h2 class="section-title">{t('fsrsAdvanced.globalTitle')}</h2>
        <p class="hint">{t('fsrsAdvanced.globalHint')}</p>

        <div class="slider-row">
          <div class="slider-head">
            <span class="label">{t('fsrsAdvanced.retentionLabel')}</span>
            <span class="value">{fmtRetention(globalRetention)}</span>
          </div>
          <input
            class="slider"
            type="range"
            min={MIN_RETENTION}
            max={MAX_RETENTION}
            step={STEP_RETENTION}
            bind:value={globalRetention}
            aria-label={t('fsrsAdvanced.retentionLabel')}
            aria-valuetext={fmtRetention(globalRetention)}
          />
        </div>

        <div class="slider-row">
          <div class="slider-head">
            <span class="label">{t('fsrsAdvanced.intervalLabel')}</span>
            <span class="value">{fmtInterval(globalInterval)}</span>
          </div>
          <input
            class="slider"
            type="range"
            min={MIN_INTERVAL}
            max={MAX_INTERVAL}
            step={STEP_INTERVAL}
            bind:value={globalInterval}
            aria-label={t('fsrsAdvanced.intervalLabel')}
            aria-valuetext={fmtInterval(globalInterval)}
          />
        </div>

        <div class="row-actions end">
          <Button
            variant="filled"
            disabled={busy || !globalDirty}
            loading={busy}
            onclick={() => void saveGlobal()}
          >
            {t('fsrsAdvanced.saveGlobal')}
          </Button>
        </div>
      </section>

      <section class="field">
        <h2 class="section-title">{t('fsrsAdvanced.subjectsTitle')}</h2>
        <p class="hint">{t('fsrsAdvanced.subjectsHint')}</p>

        {#if rows.length === 0}
          <EmptyState
            icon="sliders-horizontal"
            title={t('fsrsAdvanced.noSubjects')}
            description={t('fsrsAdvanced.noSubjectsHint')}
          />
        {:else}
          <div class="table-wrap">
            <table>
              <caption class="sr-only">{t('fsrsAdvanced.subjectsTitle')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('fsrsAdvanced.colName')}</th>
                  <th scope="col" class="num">{t('fsrsAdvanced.colRetention')}</th>
                  <th scope="col" class="num">{t('fsrsAdvanced.colInterval')}</th>
                  <th scope="col" class="actions-col"></th>
                </tr>
              </thead>
              <tbody>
                {#each rows as row (row.subject.id)}
                  <tr>
                    <td>
                      <span class="subject-name">{row.subject.title}</span>
                      {#if row.hasOverride}
                        <Badge variant="accent" shape="square" size="sm">
                          {t('fsrsAdvanced.override')}
                        </Badge>
                      {:else}
                        <Badge variant="default" shape="square" size="sm">
                          {t('fsrsAdvanced.inherited')}
                        </Badge>
                      {/if}
                    </td>
                    <td class="num mono">{fmtRetention(row.effectiveRetention)}</td>
                    <td class="num mono">{fmtInterval(row.effectiveInterval)}</td>
                    <td class="actions-col">
                      {#if row.hasOverride}
                        <Button
                          variant="plain"
                          size="sm"
                          iconOnly
                          disabled={busy}
                          ariaLabel={t('fsrsAdvanced.resetOverride')}
                          onclick={() => void resetSubjectOverride(row.subject.id)}
                        >
                          <Icon name="rotate-ccw" size={14} />
                        </Button>
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </section>

      {#if message}
        <p class="info">{message}</p>
      {/if}
    </div>
  {/if}

  {#snippet footer()}
    <Button variant="outline" disabled={busy} onclick={onClose}>
      {t('fsrsAdvanced.close')}
    </Button>
  {/snippet}
</Modal>

<style>
  .body {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }

  .loading {
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    text-align: center;
    margin: 0;
    padding: var(--space-4);
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding-bottom: var(--space-4);
    border-bottom: var(--border-width) solid var(--color-border);
  }

  .field:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
  }

  .section-title {
    margin: 0;
    font-size: var(--text-h3);
    font-weight: var(--text-h3-weight);
    letter-spacing: var(--text-h3-track);
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .slider-row {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .slider-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .label {
    font-size: var(--text-label);
    font-weight: var(--text-label-weight);
    letter-spacing: var(--text-label-track);
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  .value {
    font-size: var(--text-mono-data);
    font-weight: var(--weight-semibold);
    color: var(--color-text-primary);
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
  }

  .slider {
    width: 100%;
    accent-color: var(--color-accent);
  }

  .hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    line-height: 1.5;
  }

  .row-actions {
    display: flex;
    gap: var(--space-2);
  }

  .row-actions.end {
    justify-content: flex-end;
  }

  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--text-body);
  }

  th {
    padding: var(--space-2) var(--space-3);
    text-align: left;
    font-size: var(--text-label);
    font-weight: var(--text-label-weight);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: var(--text-label-track);
    border-bottom: var(--border-width) solid var(--color-border);
  }

  th.num {
    text-align: right;
  }

  th.actions-col,
  td.actions-col {
    width: 3rem;
    text-align: center;
  }

  td {
    padding: var(--space-3);
    color: var(--color-text-primary);
    border-bottom: var(--border-width) solid var(--color-border);
  }

  td.num {
    text-align: right;
  }

  td.mono {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    color: var(--color-text-secondary);
  }

  .subject-name {
    margin-right: var(--space-2);
  }

  .info {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-success);
    font-family: var(--font-mono);
  }

  :global(.sr-only) {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>